import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { Save, Loader2, Plus, Trash2, Upload, Image, Eye, Video, Type, Users, Handshake, LayoutGrid } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';
import RichTextEditor from '@/components/RichTextEditor';

const HomepageCMSPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('hero');
  const [hero, setHero] = useState({ title: '', subtitle: '', tagline: '', video_url: '' });
  const [about, setAbout] = useState({ title: '', paragraphs: [], closing: '', stat_regions: 12, stat_years: 25 });
  const [ctaCards, setCtaCards] = useState({ title: '', cta_text: '', cards: [], exclusive_title: '', exclusive_cards: [] });
  const [partners, setPartners] = useState({ title: '', items: [] });
  const fileRef = useRef(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const res = await axios.get(`${API}/site-content`);
      const map = {};
      res.data.forEach(s => { map[s.section] = s.data || {}; });
      if (map.hero) setHero(map.hero);
      if (map.about) setAbout(map.about);
      if (map.cta_cards) setCtaCards(map.cta_cards);
      // Normalize partners to have items[] with {name, logo_url}
      if (map.partners) {
        const p = map.partners;
        if (Array.isArray(p.logos)) {
          // Convert old format ['eclso','ldc'] to new format [{name:'eclso',logo_url:'/partner-eclso.png'}]
          const items = p.logos.map(l => typeof l === 'string' ? { name: l, logo_url: `/partner-${l}.png` } : l);
          setPartners({ title: p.title || 'Nos partenaires', items });
        } else if (p.items) {
          setPartners(p);
        } else {
          setPartners({ title: p.title || 'Nos partenaires', items: [] });
        }
      }
    } catch { /* defaults */ }
    setLoading(false);
  };

  const save = async (section, data) => {
    setSaving(true);
    try {
      let saveData = data;
      // For partners, also save in the old logos format for backward compat
      if (section === 'partners') {
        saveData = { ...data, logos: data.items.map(i => typeof i === 'string' ? i : i.name) };
      }
      await axios.put(`${API}/site-content/${section}`, { data: saveData }, { headers });
      toast.success('Sauvegardé !');
    } catch { toast.error('Erreur de sauvegarde'); }
    setSaving(false);
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/upload/cms-image`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      return res.data.url;
    } catch { toast.error('Erreur d\'upload'); return null; }
  };

  const inputClass = 'w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#35AFE4] focus:ring-2 focus:ring-[#35AFE4]/10 outline-none transition-colors';
  const labelClass = 'block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5';
  const SaveBtn = ({ onClick }) => (
    <button onClick={onClick} disabled={saving}
      className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#1e3a5f] hover:bg-[#162d4a] text-white text-sm font-bold disabled:opacity-50 transition-colors" data-testid="save-btn">
      {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Sauvegarder
    </button>
  );

  const tabs = [
    { id: 'hero', label: 'Bannière', icon: Video },
    { id: 'about', label: 'Qui sommes-nous', icon: Type },
    { id: 'cta', label: 'Espace Pro', icon: LayoutGrid },
    { id: 'partners', label: 'Partenaires', icon: Handshake },
  ];

  if (loading) return <AdminLayout><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-gray-400" size={24} /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 max-w-5xl mx-auto" data-testid="homepage-cms">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center">
            <LayoutGrid size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>Page d'accueil</h1>
            <p className="text-xs text-gray-500">Modifiez chaque section de la page d'accueil du site</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6">
          {tabs.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 flex-1 justify-center px-3 py-2.5 rounded-lg text-[13px] font-semibold transition-all ${
                  tab === t.id ? 'bg-white text-[#1e3a5f] shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`} data-testid={`tab-${t.id}`}>
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          {/* ===== HERO ===== */}
          {tab === 'hero' && (
            <div className="space-y-5" data-testid="section-hero">
              <p className="text-sm text-gray-500 mb-2 flex items-center gap-2"><Eye size={14} /> Aperçu : la bannière principale avec vidéo de fond</p>
              <div>
                <label className={labelClass}>Sous-titre (au-dessus du titre)</label>
                <input value={hero.subtitle} onChange={e => setHero(p => ({ ...p, subtitle: e.target.value }))} className={inputClass}
                  placeholder="Bienvenue sur le site de la SFOALC" data-testid="hero-subtitle" />
              </div>
              <div>
                <label className={labelClass}>Titre principal</label>
                <input value={hero.title} onChange={e => setHero(p => ({ ...p, title: e.target.value }))} className={inputClass}
                  placeholder="Société Française des Ophtalmologistes..." data-testid="hero-title" />
              </div>
              <div>
                <label className={labelClass}>Tagline (phrase en italique sous le titre)</label>
                <input value={hero.tagline} onChange={e => setHero(p => ({ ...p, tagline: e.target.value }))} className={inputClass}
                  placeholder="L'adaptation des lentilles de contact est un acte médical" data-testid="hero-tagline" />
              </div>
              <div>
                <label className={labelClass}>URL de la vidéo YouTube (embed)</label>
                <input value={hero.video_url} onChange={e => setHero(p => ({ ...p, video_url: e.target.value }))} className={inputClass}
                  placeholder="https://www.youtube.com/embed/..." data-testid="hero-video" />
                {hero.video_url && (
                  <div className="mt-3 rounded-xl overflow-hidden border border-gray-200 aspect-video max-w-sm">
                    <iframe src={hero.video_url} className="w-full h-full" title="Aperçu vidéo" allow="autoplay; encrypted-media" />
                  </div>
                )}
              </div>
              <SaveBtn onClick={() => save('hero', hero)} />
            </div>
          )}

          {/* ===== ABOUT ===== */}
          {tab === 'about' && (
            <div className="space-y-5" data-testid="section-about">
              <p className="text-sm text-gray-500 mb-2 flex items-center gap-2"><Eye size={14} /> Section "Qui sommes-nous" avec texte + chiffres clés</p>
              <div>
                <label className={labelClass}>Titre</label>
                <input value={about.title} onChange={e => setAbout(p => ({ ...p, title: e.target.value }))} className={inputClass}
                  placeholder="Qui sommes-nous ?" data-testid="about-title" />
              </div>
              <div>
                <label className={labelClass}>Contenu "Qui sommes-nous"</label>
                <RichTextEditor
                  value={(about.paragraphs || []).join('')}
                  onChange={val => setAbout(prev => ({ ...prev, paragraphs: [val] }))}
                  placeholder="Présentez la SFOALC avec un texte riche formaté..."
                  height="200px"
                />
              </div>
              <div>
                <label className={labelClass}>Phrase de conclusion (en gras)</label>
                <input value={about.closing} onChange={e => setAbout(p => ({ ...p, closing: e.target.value }))} className={inputClass}
                  placeholder="Alors gardons le contact..." data-testid="about-closing" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Nombre de régions</label>
                  <input type="number" value={about.stat_regions || 0} onChange={e => setAbout(p => ({ ...p, stat_regions: parseInt(e.target.value) || 0 }))} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Années d'existence</label>
                  <input type="number" value={about.stat_years || 0} onChange={e => setAbout(p => ({ ...p, stat_years: parseInt(e.target.value) || 0 }))} className={inputClass} />
                </div>
              </div>
              <SaveBtn onClick={() => save('about', about)} />
            </div>
          )}

          {/* ===== CTA CARDS ===== */}
          {tab === 'cta' && (
            <div className="space-y-5" data-testid="section-cta">
              <p className="text-sm text-gray-500 mb-2 flex items-center gap-2"><Eye size={14} /> Section bleue "Vous êtes Ophtalmologiste ?" avec cartes</p>
              <div>
                <label className={labelClass}>Titre principal</label>
                <input value={ctaCards.title} onChange={e => setCtaCards(p => ({ ...p, title: e.target.value }))} className={inputClass}
                  placeholder="Vous êtes Ophtalmologiste ?" data-testid="cta-title" />
              </div>
              <div>
                <label className={labelClass}>Texte du bouton CTA</label>
                <input value={ctaCards.cta_text} onChange={e => setCtaCards(p => ({ ...p, cta_text: e.target.value }))} className={inputClass}
                  placeholder="Rejoignez-nous !" />
              </div>
              <div>
                <label className={labelClass}>Cartes principales ({(ctaCards.cards || []).length})</label>
                <div className="space-y-3">
                  {(ctaCards.cards || []).map((card, i) => (
                    <div key={i} className="bg-gray-50 rounded-xl p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-gray-400">Carte {i + 1}</span>
                        <button onClick={() => setCtaCards(p => ({ ...p, cards: p.cards.filter((_, j) => j !== i) }))}
                          className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                      </div>
                      <input value={card.title || ''} onChange={e => {
                        const updated = [...ctaCards.cards]; updated[i] = { ...updated[i], title: e.target.value };
                        setCtaCards(p => ({ ...p, cards: updated }));
                      }} className={inputClass} placeholder="Titre de la carte" />
                      <RichTextEditor value={card.desc || ''} onChange={val => {
                        const updated = [...ctaCards.cards]; updated[i] = { ...updated[i], desc: val };
                        setCtaCards(p => ({ ...p, cards: updated }));
                      }} placeholder="Description de la carte" height="80px" />
                      <input value={card.btn || ''} onChange={e => {
                        const updated = [...ctaCards.cards]; updated[i] = { ...updated[i], btn: e.target.value };
                        setCtaCards(p => ({ ...p, cards: updated }));
                      }} className={inputClass} placeholder="Texte du bouton" />
                    </div>
                  ))}
                  <button onClick={() => setCtaCards(p => ({ ...p, cards: [...(p.cards || []), { title: '', desc: '', btn: '' }] }))}
                    className="flex items-center gap-1 px-3 py-2 border border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:text-gray-700" data-testid="add-cta-card">
                    <Plus size={14} /> Ajouter une carte
                  </button>
                </div>
              </div>
              <div>
                <label className={labelClass}>Titre contenu exclusif</label>
                <input value={ctaCards.exclusive_title || ''} onChange={e => setCtaCards(p => ({ ...p, exclusive_title: e.target.value }))} className={inputClass}
                  placeholder="et accédez à un contenu exclusif" />
              </div>
              <div>
                <label className={labelClass}>Cartes exclusives ({(ctaCards.exclusive_cards || []).length})</label>
                <div className="space-y-3">
                  {(ctaCards.exclusive_cards || []).map((card, i) => (
                    <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                      <input value={card.icon || ''} onChange={e => {
                        const updated = [...ctaCards.exclusive_cards]; updated[i] = { ...updated[i], icon: e.target.value };
                        setCtaCards(p => ({ ...p, exclusive_cards: updated }));
                      }} className={inputClass + ' w-36'} placeholder="fa-icon" />
                      <input value={card.title || ''} onChange={e => {
                        const updated = [...ctaCards.exclusive_cards]; updated[i] = { ...updated[i], title: e.target.value };
                        setCtaCards(p => ({ ...p, exclusive_cards: updated }));
                      }} className={inputClass + ' flex-1'} placeholder="Titre" />
                      <button onClick={() => setCtaCards(p => ({ ...p, exclusive_cards: p.exclusive_cards.filter((_, j) => j !== i) }))}
                        className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                    </div>
                  ))}
                  <button onClick={() => setCtaCards(p => ({ ...p, exclusive_cards: [...(p.exclusive_cards || []), { icon: '', title: '' }] }))}
                    className="flex items-center gap-1 px-3 py-2 border border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:text-gray-700">
                    <Plus size={14} /> Ajouter une carte exclusive
                  </button>
                </div>
              </div>
              <SaveBtn onClick={() => save('cta_cards', ctaCards)} />
            </div>
          )}

          {/* ===== PARTNERS ===== */}
          {tab === 'partners' && (
            <div className="space-y-5" data-testid="section-partners">
              <p className="text-sm text-gray-500 mb-2 flex items-center gap-2"><Eye size={14} /> Logos des partenaires affichés en bas de page</p>
              <div>
                <label className={labelClass}>Titre de la section</label>
                <input value={partners.title} onChange={e => setPartners(p => ({ ...p, title: e.target.value }))} className={inputClass}
                  placeholder="Nos partenaires" data-testid="partners-title" />
              </div>
              <div>
                <label className={labelClass}>Logos partenaires ({partners.items.length})</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {partners.items.map((item, i) => (
                    <div key={i} className="relative group bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 p-4 text-center hover:border-[#35AFE4]/30 transition-colors">
                      <div className="w-full h-20 flex items-center justify-center mb-3">
                        <img src={item.logo_url} alt={item.name} className="max-h-16 max-w-full object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                      </div>
                      <input value={item.name || ''} onChange={e => {
                        const updated = [...partners.items]; updated[i] = { ...updated[i], name: e.target.value };
                        setPartners(p => ({ ...p, items: updated }));
                      }} className="w-full px-2 py-1 text-xs text-center rounded border border-gray-200 bg-white" placeholder="Nom du partenaire" />
                      <input value={item.logo_url || ''} onChange={e => {
                        const updated = [...partners.items]; updated[i] = { ...updated[i], logo_url: e.target.value };
                        setPartners(p => ({ ...p, items: updated }));
                      }} className="w-full px-2 py-1 text-[10px] text-center rounded border border-gray-200 bg-white mt-1 text-gray-400" placeholder="URL du logo" />
                      <div className="flex items-center justify-center gap-2 mt-2">
                        <label className="cursor-pointer px-2 py-1 bg-[#eaf6fc] text-[#35AFE4] rounded text-[10px] font-bold hover:bg-[#d5ebf2] flex items-center gap-1">
                          <Upload size={10} /> Charger
                          <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            const url = await uploadImage(file);
                            if (url) {
                              const updated = [...partners.items]; updated[i] = { ...updated[i], logo_url: url };
                              setPartners(p => ({ ...p, items: updated }));
                              toast.success('Logo chargé');
                            }
                          }} />
                        </label>
                        <button onClick={() => setPartners(p => ({ ...p, items: p.items.filter((_, j) => j !== i) }))}
                          className="px-2 py-1 text-red-400 hover:text-red-600 text-[10px]"><Trash2 size={10} /></button>
                      </div>
                    </div>
                  ))}
                  <button onClick={() => setPartners(p => ({ ...p, items: [...p.items, { name: '', logo_url: '' }] }))}
                    className="flex flex-col items-center justify-center gap-2 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 p-6 hover:border-[#35AFE4]/40 hover:bg-[#f4fafe] transition-colors cursor-pointer min-h-[160px]" data-testid="add-partner">
                    <Plus size={24} className="text-gray-400" />
                    <span className="text-xs text-gray-400 font-semibold">Ajouter un partenaire</span>
                  </button>
                </div>
              </div>
              <SaveBtn onClick={() => save('partners', partners)} />
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default HomepageCMSPage;
